import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 980,
    margin: "0 auto"
  },
  hero: {
    padding: theme.spacing(2.5),
    borderRadius: 18,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  heroTitle: {
    fontWeight: 800,
    color: theme.palette.text.primary
  },
  heroSubtitle: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary
  },
  section: {
    borderRadius: 16,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5)
  },
  sectionHint: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  toggleCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    padding: theme.spacing(1.5, 1.75),
    marginBottom: theme.spacing(1.5),
    backgroundColor:
      theme.mode === "light" ? "#f8fbff" : theme.palette.background.default
  },
  toggleLabel: {
    margin: 0,
    alignItems: "flex-start"
  },
  toggleControl: {
    marginRight: theme.spacing(1.25),
    marginLeft: -2
  },
  toggleTextWrap: {
    marginTop: 2
  },
  toggleLabelText: {
    fontWeight: 700,
    fontSize: "0.92rem",
    color: theme.palette.text.primary
  },
  toggleHelp: {
    marginTop: theme.spacing(0.5),
    marginLeft: 0,
    color: theme.palette.text.secondary,
    fontSize: "0.82rem"
  },
  fieldLabel: {
    fontWeight: 600
  },
  messageBox: {
    marginTop: theme.spacing(1)
  },
  saveWrap: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2)
  },
  save: {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    padding: theme.spacing(1, 2.5)
  }
}));

const IOSSwitch = withStyles(theme => ({
  root: {
    width: 42,
    height: 26,
    padding: 0
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: "none"
      }
    },
  },
  thumb: {
    width: 24,
    height: 24
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.mode === "light" ? "#d1d5db" : "#4b5563",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"])
  },
  checked: {}
}))(Switch);

const BirthdaySettings = () => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    userBirthdayEnabled: true,
    contactBirthdayEnabled: true,
    contactBirthdayMessage: "",
    sendBirthdayTime: "09:00:00",
    sendIntervalSeconds: 0,
    whatsappId: ""
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/birthdays/settings");
        const s = data?.data || {};
        setSettings({
          userBirthdayEnabled: !!s.userBirthdayEnabled,
          contactBirthdayEnabled: !!s.contactBirthdayEnabled,
          contactBirthdayMessage: s.contactBirthdayMessage || "",
          sendBirthdayTime: s.sendBirthdayTime || "09:00:00",
          sendIntervalSeconds: Number.isFinite(Number(s.sendIntervalSeconds))
            ? Number(s.sendIntervalSeconds)
            : 0,
          whatsappId: s.whatsappId || ""
        });
      } catch (err) {
        toast.error("Erro ao carregar configurações de aniversário");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/birthdays/settings", {
        ...settings,
        whatsappId: settings.whatsappId || null
      });
      toast.success("Configurações salvas com sucesso");
    } catch (err) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Typography>Carregando...</Typography>;

  return (
    <Box className={classes.root}>
      <Paper className={classes.hero} elevation={0}>
        <Typography variant="h5" className={classes.heroTitle}>
          Configurações de Aniversário
        </Typography>
        <Typography variant="body2" className={classes.heroSubtitle}>
          Defina como o sistema deve tratar aniversários de usuários e contatos.
        </Typography>
      </Paper>

      <Paper className={classes.section} elevation={0}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Preferências
        </Typography>
        <Typography variant="body2" className={classes.sectionHint}>
          Ative apenas o que fizer sentido para sua operação.
        </Typography>

        <div className={classes.toggleCard}>
          <FormControlLabel
            className={classes.toggleLabel}
            control={
              <IOSSwitch
                className={classes.toggleControl}
                checked={settings.userBirthdayEnabled}
                onChange={e => update("userBirthdayEnabled", e.target.checked)}
              />
            }
            label={
              <div className={classes.toggleTextWrap}>
                <Typography className={classes.toggleLabelText}>
                  Avisos de aniversário de usuários
                </Typography>
                <Typography className={classes.toggleHelp}>
                  Mostra no sistema quando alguém da equipe fizer aniversário no dia.
                </Typography>
              </div>
            }
          />
        </div>

        <div className={classes.toggleCard}>
          <FormControlLabel
            className={classes.toggleLabel}
            control={
              <IOSSwitch
                className={classes.toggleControl}
                checked={settings.contactBirthdayEnabled}
                onChange={e => update("contactBirthdayEnabled", e.target.checked)}
              />
            }
            label={
              <div className={classes.toggleTextWrap}>
                <Typography className={classes.toggleLabelText}>
                  Envio automático para contatos
                </Typography>
                <Typography className={classes.toggleHelp}>
                  Envia mensagem de parabéns no WhatsApp para contatos aniversariantes.
                </Typography>
              </div>
            }
          />
        </div>

        <Grid container spacing={2} className={classes.messageBox}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Mensagem para contatos"
              value={settings.contactBirthdayMessage}
              onChange={e => update("contactBirthdayMessage", e.target.value)}
              helperText="Use {nome} para incluir o nome do contato"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="time"
              label="Horário do envio automático"
              value={String(settings.sendBirthdayTime || "09:00:00").slice(0, 5)}
              onChange={e => update("sendBirthdayTime", `${e.target.value}:00`)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="number"
              label="Intervalo entre envios (segundos)"
              value={settings.sendIntervalSeconds}
              onChange={e =>
                update(
                  "sendIntervalSeconds",
                  Math.max(0, Number(e.target.value || 0))
                )
              }
              inputProps={{ min: 0, max: 3600 }}
              helperText="0 = envia sem intervalo"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Conexão WhatsApp</InputLabel>
              <Select
                value={settings.whatsappId}
                onChange={e => update("whatsappId", e.target.value)}
              >
                <MenuItem value="">
                  <em>Padrão da empresa</em>
                </MenuItem>
                {whatsApps?.map(w => (
                  <MenuItem value={w.id} key={w.id}>
                    {w.name} ({w.status})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box className={classes.saveWrap}>
          <Button
            variant="contained"
            color="primary"
            className={classes.save}
            disabled={saving}
            onClick={save}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BirthdaySettings;
